import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface DarkModeContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  setDarkMode: (dark: boolean) => void;
  isSystemPreference: boolean;
  setSystemPreference: (useSystem: boolean) => void;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

interface DarkModeProviderProps {
  children: ReactNode;
}

export const DarkModeProvider: React.FC<DarkModeProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isSystemPreference, setIsSystemPreference] = useState(true);

  // Initialize theme on component mount
  useEffect(() => {
    // Load saved preferences from localStorage
    const savedDarkMode = localStorage.getItem('darkMode');
    const savedSystemPreference = localStorage.getItem('useSystemPreference');

    if (savedSystemPreference !== null) {
      const useSystem = JSON.parse(savedSystemPreference);
      setIsSystemPreference(useSystem);
      
      if (useSystem) {
        // Use system preference
        const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setIsDarkMode(systemPrefersDark);
      } else {
        // Use saved user preference
        const savedDark = JSON.parse(savedDarkMode || 'false');
        setIsDarkMode(savedDark);
      }
    } else {
      // First time user - default to system preference
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(systemPrefersDark);
    }
  }, []);

  // Listen for system preference changes
  useEffect(() => {
    if (!isSystemPreference) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [isSystemPreference]);

  // Apply theme to document when dark mode changes
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    
    if (!isSystemPreference) {
      localStorage.setItem('darkMode', JSON.stringify(newDarkMode));
    }
  };

  // Choosing light or dark is a manual choice: it also stops following the device.
  const setDarkMode = (dark: boolean) => {
    setIsDarkMode(dark);
    setIsSystemPreference(false);
    try {
      localStorage.setItem('useSystemPreference', 'false');
      localStorage.setItem('darkMode', JSON.stringify(dark));
    } catch { /* private mode: the choice lasts until reload */ }
  };

  const setSystemPreference = (useSystem: boolean) => {
    setIsSystemPreference(useSystem);
    localStorage.setItem('useSystemPreference', JSON.stringify(useSystem));
    
    if (useSystem) {
      // Switch to system preference
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(systemPrefersDark);
      localStorage.removeItem('darkMode'); // Clear saved preference
    }
  };

  const value: DarkModeContextType = {
    isDarkMode,
    toggleDarkMode,
    setDarkMode,
    isSystemPreference,
    setSystemPreference,
  };

  return (
    <DarkModeContext.Provider value={value}>
      {children}
    </DarkModeContext.Provider>
  );
};

export const useDarkMode = (): DarkModeContextType => {
  const context = useContext(DarkModeContext);
  if (context === undefined) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};
